import { useEffect, useRef, useState } from "react";
import {
  analyzeRea, cancelReaJob, createReaJob, generateRea, getReaAgentTeam, getReaJob, getReaStatus, listReaJobs, ReaClientError,
  type ReaAgentJob, type ReaAgentJobStatus, type ReaAgentRegistry, type ReaAgentStep,
  type ReaElement, type ReaEvidence, type ReaGenerated, type ReaManifest,
  type ReaKind, type ReaPlatform, type ReaReport, type ReaScreen, type ReaStatus
} from "../../api/rea-client";
import { readStoredSession } from "../../api/auth-client";
import "./creator-rea-studio.css";
import { CreatorReaAgents } from "./creator-rea-agents";
import { REA_OMNI_WIKI_GOAL, REA_OMNI_WIKI_OBSERVATIONS,
  REA_OMNI_WIKI_REFERENCE_NAME } from "./rea-omni-wiki-template";

type Props = { userId: string; mode?: "tools" | "agents" };
const example = [
  "Pantalla: Inicio",
  "input | Instrucciones | Recibir una descripción | observed",
  "button | Crear | Solicitar nueva creación | observed",
  "button | Biblioteca | Abrir los proyectos | documentation"
].join("\n");
const kinds: Record<string, ReaKind> = {
  button: "button", boton: "button", input: "input", campo: "input",
  toggle: "toggle", interruptor: "toggle", tab: "tab", pestana: "tab",
  menu: "menu", imagen: "image", image: "image",
  texto: "text", text: "text", lista: "list", list: "list"
};
const evidences: Record<string, ReaEvidence> = {
  observed: "observed", observado: "observed", observada: "observed",
  documentation: "documentation", documentacion: "documentation",
  hypothesis: "hypothesis", hipotesis: "hypothesis"
};
function normalized(value: string) {
  return value.trim().toLowerCase().normalize("NFD").replace(/\p{Diacritic}/gu, "");
}
export function parseReaObservations(value: string): ReaScreen[] {
  const screens: ReaScreen[] = [];
  for (const [index, rawLine] of value.split(/\r?\n/).entries()) {
    const line = rawLine.trim();
    if (!line) continue;
    if (/^pantalla\s*:/i.test(line)) {
      const name = line.replace(/^pantalla\s*:/i, "").trim();
      if (!name) throw new Error("La pantalla de la línea " + (index + 1) + " necesita nombre.");
      screens.push({ name, description: "", elements: [] });
      continue;
    }
    if (screens.length === 0) throw new Error("Escribe primero Pantalla: Nombre.");
    const parts = line.split("|").map(part => part.trim());
    if (parts.length < 2 || parts.length > 4) {
      throw new Error("Revisa el formato en la línea " + (index + 1) + ": tipo | título | acción | evidencia.");
    }
    const kind = kinds[normalized(parts[0])];
    if (!kind) throw new Error("Tipo desconocido en la línea " + (index + 1) + ".");
    if (!parts[1]) throw new Error("El elemento de la línea " + (index + 1) + " necesita título.");
    const evidence = parts[3] ? evidences[normalized(parts[3])] : "hypothesis";
    if (!evidence) throw new Error("Evidencia desconocida en la línea " + (index + 1) + ".");
    const element: ReaElement = { kind, label: parts[1], action: parts[2] ?? "", evidence };
    screens[screens.length - 1].elements.push(element);
  }
  if (!screens.length) throw new Error("Añade al menos una pantalla.");
  if (screens.some(screen => screen.elements.length === 0)) {
    throw new Error("Cada pantalla necesita al menos un elemento.");
  }
  if (screens.length > 8 || screens.some(screen => screen.elements.length > 30)) {
    throw new Error("Máximo 8 pantallas y 30 elementos por pantalla.");
  }
  return screens;
}

const agentDefinitions = [
  { id: "architect", name: "Arquitecto", description: "Ordena las funciones y sus evidencias." },
  { id: "builder", name: "Constructor", description: "Prepara el prototipo propio de OMNISYNIA." },
  { id: "verifier", name: "Verificador", description: "Comprueba su estructura y detecta pendientes." },
  { id: "guard", name: "Guardián", description: "Revisa el alcance y entrega el resultado." }
] as const;
const jobLabels: Record<ReaAgentJobStatus, string> = {
  queued: "En cola", running: "Trabajando", completed: "Trabajo preparado",
  needs_attention: "Requiere revisión", failed: "Trabajo fallido", cancelled: "Cancelado"
};
const stepLabels: Record<ReaAgentStep["status"], string> = {
  pending: "Pendiente", running: "Trabajando", completed: "Completado", failed: "Requiere revisión", cancelled: "Cancelado"
};
function activeJob(job: ReaAgentJob | null) { return job?.status === "queued" || job?.status === "running"; }
const monitoringDuration = 120_000;

// Changing account destroys every draft, controller and result owned by the previous account.
export function CreatorReaStudio({ userId, mode = "tools" }: Props) {
  return <ReaStudioSession key={userId + ":" + mode} userId={userId} mode={mode} />;
}

function ReaStudioSession({ userId, mode }: Props) {
  const [registry, setRegistry] = useState<ReaAgentRegistry | null>(null);
  const [coreStatus, setCoreStatus] = useState<ReaStatus | null>(null);
  const [specialistSessionVersion, setSpecialistSessionVersion] = useState(0);
  const [appName, setAppName] = useState("");
  const [platform, setPlatform] = useState<ReaPlatform>("web");
  const [goal, setGoal] = useState("");
  const [observations, setObservations] = useState(example);
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [report, setReport] = useState<ReaReport | null>(null);
  const [generated, setGenerated] = useState<ReaGenerated | null>(null);
  const [jobs, setJobs] = useState<ReaAgentJob[]>([]);
  const [job, setJob] = useState<ReaAgentJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [following, setFollowing] = useState(false);
  const [notice, setNotice] = useState("");
  const [pageVisible, setPageVisible] = useState(() => !document.hidden);
  const [inViewport, setInViewport] = useState(true);
  const studio = useRef<HTMLElement | null>(null);
  const currentRequest = useRef<AbortController | null>(null);
  const locked = useRef(false);
  const alive = useRef(false);
  const monitoringUntil = useRef(0);
  const downloadUrls = useRef(new Map<string, number>());
  const running = activeJob(job);
  const engine = mode === "agents" ? registry : coreStatus;
  const formLocked = busy || running;
  const jobId = job?.id;

  function beginRequest() {
    currentRequest.current?.abort();
    const controller = new AbortController();
    currentRequest.current = controller;
    return controller;
  }
  function isCurrent(controller: AbortController) {
    return alive.current && currentRequest.current === controller && !controller.signal.aborted;
  }
  function forgetResults() {
    setJob(null); setJobs([]); setReport(null); setGenerated(null); setFollowing(false);
    setSpecialistSessionVersion(version => version + 1);
  }
  function showError(error: unknown, fallback: string, missingJobId?: string) {
    const missingOtherJob = error instanceof ReaClientError && error.status === 404
      && missingJobId !== undefined && missingJobId !== jobId;
    if (!missingOtherJob) setFollowing(false);
    if (error instanceof ReaClientError && (error.status === 401 || error.status === 403)) {
      setRegistry(null); setCoreStatus(null); forgetResults();
    }
    if (error instanceof ReaClientError && error.status === 404 && missingJobId) {
      if (missingJobId === jobId) { setJob(null); setReport(null); setGenerated(null); }
      setJobs(previous => previous.filter(item => item.id !== missingJobId));
    }
    setNotice(error instanceof TypeError || (error instanceof Error && error.name === "TimeoutError")
      ? fallback : error instanceof Error ? error.message : fallback);
  }
  function acceptJob(result: ReaAgentJob) {
    setJob(result);
    setReport(result.report ?? null);
    setGenerated(result.generated ?? null);
    setJobs(previous => [result, ...previous.filter(item => item.id !== result.id)].slice(0, 8));
  }
  function followJob(result: ReaAgentJob) {
    acceptJob(result);
    monitoringUntil.current = Date.now() + monitoringDuration;
    setFollowing(activeJob(result));
  }
  async function perform(operation: (controller: AbortController) => Promise<void>, fallback: string, missingJobId?: string) {
    if (locked.current) return;
    locked.current = true;
    setBusy(true); setNotice("");
    const controller = beginRequest();
    try { await operation(controller); }
    catch (error) { if (isCurrent(controller)) showError(error, fallback, missingJobId); }
    finally {
      if (isCurrent(controller)) {
        currentRequest.current = null;
        locked.current = false;
        setBusy(false);
      }
    }
  }
  async function loadWorkspace() {
    await perform(async controller => {
      if (mode !== "agents") {
        const result = await getReaStatus(userId, controller.signal);
        if (isCurrent(controller)) setCoreStatus(result);
        return;
      }
      const result = await getReaAgentTeam(userId, controller.signal);
      if (!isCurrent(controller)) return;
      setRegistry(result);
      const history = await listReaJobs(userId, controller.signal);
      if (!isCurrent(controller)) return;
      setJobs(history.jobs.slice(0, 8));
      const latest = history.jobs.find(item => item.id === jobId)
        ?? history.jobs.find(item => activeJob(item)) ?? history.jobs[0];
      if (latest) followJob(latest);
      else { setJob(null); setReport(null); setGenerated(null); setFollowing(false); }
    }, "No se pudo consultar el equipo REA.");
  }

  useEffect(() => {
    alive.current = true;
    locked.current = false;
    void loadWorkspace();
    const initialToken = readStoredSession()?.sessionToken;
    function sessionChanged() {
      const session = readStoredSession();
      if (session?.user.id === userId && session.user.role === "creator"
          && session.sessionToken === initialToken && Date.parse(session.expiresAt) > Date.now()) return;
      currentRequest.current?.abort();
      currentRequest.current = null;
      locked.current = false;
      setBusy(false); setRegistry(null); setCoreStatus(null); forgetResults();
      setAppName(""); setGoal(""); setObservations(example); setRightsConfirmed(false); setPlatform("web");
      setNotice("La sesión ha cambiado. Vuelve a abrir Agentes desde la cuenta del Creador.");
    }
    function visibilityChanged() { setPageVisible(!document.hidden); }
    document.addEventListener("visibilitychange", visibilityChanged);
    window.addEventListener("omnisynia:session-change", sessionChanged);
    window.addEventListener("storage", sessionChanged);
    const observer = typeof IntersectionObserver === "undefined" ? null
      : new IntersectionObserver(entries => setInViewport(entries.some(entry => entry.isIntersecting)));
    if (studio.current) observer?.observe(studio.current);
    const urls = downloadUrls.current;
    return () => {
      alive.current = false;
      currentRequest.current?.abort();
      document.removeEventListener("visibilitychange", visibilityChanged);
      window.removeEventListener("omnisynia:session-change", sessionChanged);
      window.removeEventListener("storage", sessionChanged);
      observer?.disconnect();
      for (const [url, timer] of urls) { window.clearTimeout(timer); URL.revokeObjectURL(url); }
      urls.clear();
    };
    // userId is also the session component key. Bootstrap never reads mutable form values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode]);

  useEffect(() => {
    if (mode !== "agents" || !jobId || !running || !following || busy || !pageVisible || !inViewport) return;
    let stopped = false;
    let timer: number | undefined;
    let controller: AbortController | undefined;
    async function poll() {
      if (stopped) return;
      if (Date.now() >= monitoringUntil.current) {
        setFollowing(false);
        setNotice("Seguimiento automático pausado. Pulsa Consultar trabajo para actualizarlo; la tarea puede seguir en el servidor.");
        return;
      }
      controller = beginRequest();
      const request = controller;
      let continuePolling = false;
      try {
        const result = await getReaJob(userId, jobId!, request.signal);
        if (stopped || !isCurrent(request)) return;
        acceptJob(result);
        continuePolling = activeJob(result);
        if (!continuePolling) setFollowing(false);
      } catch (error) {
        if (!stopped && isCurrent(request)) showError(error, "No se pudo actualizar el trabajo. Pulsa Consultar trabajo para intentarlo de nuevo.", jobId);
      } finally {
        if (currentRequest.current === request) currentRequest.current = null;
        // Schedule only after the previous response. Errors and terminal states do not retry.
        if (!stopped && continuePolling) timer = window.setTimeout(() => void poll(), 1_000);
      }
    }
    timer = window.setTimeout(() => void poll(), 1_000);
    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      controller?.abort();
    };
    // The polled identity is stable; result state is updated through functional setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode, jobId, running, following, busy, pageVisible, inViewport]);

  function invalidate() {
    setReport(null); setGenerated(null); setJob(null); setFollowing(false); setNotice("");
  }
  function manifest(): ReaManifest {
    if (!appName.trim() || !goal.trim()) throw new Error("Indica la referencia y la función que quieres desarrollar.");
    return { appName, platform, goal, rightsConfirmed, screens: parseReaObservations(observations) };
  }
  function loadOmniWikiReference() {
    setAppName(REA_OMNI_WIKI_REFERENCE_NAME); setPlatform("web");
    setGoal(REA_OMNI_WIKI_GOAL); setObservations(REA_OMNI_WIKI_OBSERVATIONS);
    setRightsConfirmed(false); invalidate();
    setNotice("Referencia OMNI WIKI 1884 cargada. Revisa las observaciones y confirma los permisos antes de ejecutar REA.");
  }
  function loadElevenLabsReference() {
    if (formLocked) return;
    setAppName("ElevenLabs — Voice Library (referencia pública)");
    setPlatform("web");
    setGoal("Investigar metadatos públicos y condiciones de acceso a voces españolas en ElevenLabs para diseñar el catálogo original REA/OMNI VOZ; mantener Kokoro local y no descargar, copiar ni entrenar voces protegidas.");
    setObservations([
      "Pantalla: Voice Library — funciones documentadas",
      "input | Buscar voz | Buscar por nombre o categoría | documentation",
      "tab | Idioma | Filtrar voces por español | documentation",
      "tab | Género | Filtrar voces femeninas y masculinas | documentation",
      "menu | Acento | Filtrar variedades de español | documentation",
      "text | Condiciones y tarifa | Examinar requisitos y multiplicadores de acceso | documentation",
      "Pantalla: OMNI VOZ — catálogo propio de referencias",
      "input | Importar catálogo JSON | Leer metadatos oficiales sin enviar clave desde la app | documentation",
      "list | Voces referenciadas | Mostrar género, acento y estado de revisión | documentation",
      "button | Revisar derechos | Comprobar licencia y coste antes de cualquier activación | hypothesis"
    ].join("\n"));
    setRightsConfirmed(false); invalidate();
    setNotice("Referencia pública ElevenLabs cargada. REA no reproduce, clona ni descarga voces; confirma los permisos antes de analizar.");
  }
  async function startAgents() {
    if (running || !registry) return;
    await perform(async controller => {
      const input = manifest();
      setReport(null); setGenerated(null); setJob(null); setFollowing(false);
      const result = await createReaJob(userId, input, controller.signal);
      if (!isCurrent(controller)) return;
      followJob(result);
      setNotice("Trabajo recibido por ONE CORE. Puedes seguir las cuatro etapas aquí.");
    }, "No se pudo confirmar el envío. Actualiza los trabajos antes de volver a enviarlo.");
  }
  async function consultJob(id: string) {
    await perform(async controller => {
      const result = await getReaJob(userId, id, controller.signal);
      if (isCurrent(controller)) followJob(result);
    }, "No se pudo consultar el trabajo.", id);
  }
  async function cancelJob() {
    if (!job || !running) return;
    const id = job.id;
    await perform(async controller => {
      setFollowing(false);
      const result = await cancelReaJob(userId, id, controller.signal);
      if (!isCurrent(controller)) return;
      followJob(result);
      setNotice(result.status === "cancelled" ? "Trabajo cancelado." : "Estado del trabajo actualizado.");
    }, "No se pudo confirmar la cancelación. Consulta el trabajo para comprobar su estado.", id);
  }
  async function analyze() {
    if (running) return;
    await perform(async controller => {
      const input = manifest();
      invalidate();
      const result = await analyzeRea(userId, input, controller.signal);
      if (!isCurrent(controller)) return;
      setReport(result);
      setNotice("Plano inicial creado. Revisa las acciones y sus evidencias antes de generar código.");
    }, "Error de análisis.");
  }
  async function generate() {
    if (!report || running) return;
    const id = report.analysisId;
    await perform(async controller => {
      const result = await generateRea(userId, id, controller.signal);
      if (!isCurrent(controller)) return;
      setGenerated(result);
      setNotice("Prototipo TSX preparado. Su ejecución y conexión a ONE CORE siguen pendientes.");
    }, "Error de generación.");
  }
  function download() {
    if (!generated) return;
    const url = URL.createObjectURL(new Blob([generated.artifact.content], { type: "text/plain;charset=utf-8" }));
    const link = document.createElement("a");
    link.href = url; link.download = generated.artifact.filename;
    document.body.appendChild(link); link.click(); link.remove();
    const timer = window.setTimeout(() => { URL.revokeObjectURL(url); downloadUrls.current.delete(url); }, 2_000);
    downloadUrls.current.set(url, timer);
  }
  async function copy() {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.artifact.content);
      if (alive.current) setNotice("Código TSX copiado.");
    } catch { if (alive.current) setNotice("No se pudo copiar. Utiliza Descargar TSX."); }
  }

  return <section ref={studio} className="rea-studio" aria-label="REA dentro de ONE CORE">
    <div className="rea-heading">
      <div><p>ONE CORE · {mode === "agents" ? "AGENTES PROPIOS" : "HERRAMIENTAS"}</p>
        <h2>{mode === "agents" ? "Agentes REA" : "REA"}</h2>
        <strong>Reconstrucción de Experiencias y Aplicaciones</strong></div>
      <span className="rea-engine-state">{engine ? "MOTOR " + engine.version : "Sin conexión verificada"}</span>
    </div>
    <p>{mode === "agents"
      ? "Describe lo que quieres construir. Nuestro equipo REA ordena las funciones, prepara un prototipo y revisa su estructura dentro del servidor de OMNISYNIA."
      : "Analiza funciones que hayas documentado y genera un prototipo propio de OMNISYNIA. Esta versión no inspecciona por sí sola aplicaciones instaladas ni interpreta imágenes automáticamente."}</p>
    {registry && <p className="rea-local">Ejecución local en ONE CORE · Sin proveedores de IA externos.
      Los trabajos caducan a los {registry.retentionMinutes} minutos; descarga el resultado para guardarlo.</p>}
    {mode === "agents" && <ol className="rea-agent-team" aria-label="Equipo de agentes propios">
      {agentDefinitions.map(agent => {
        const step = job?.steps.find(item => item.id === agent.id);
        return <li key={agent.id} data-agent={agent.id} data-status={step?.status ?? "pending"}>
          <strong>{agent.name}</strong><span>{agent.description}</span>
          <b>{step ? stepLabels[step.status] : registry ? "En espera" : "Sin conexión"}</b>
          {step?.detail && <small>{step.detail}</small>}
        </li>;
      })}
    </ol>}
    {mode !== "agents" && <CreatorReaAgents key={userId + ":" + specialistSessionVersion} userId={userId} />}
    <details className="rea-scope"><summary>Qué cubre este trabajo</summary>
      <p>REA utiliza las funciones y evidencias que documentes. Esta versión no inspecciona aplicaciones
        instaladas ni interpreta imágenes automáticamente. La revisión del prototipo es estructural;
        su ejecución, conexión de botones a operaciones reales y publicación requieren una fase posterior.</p>
    </details>
    <div className="rea-actions">
      <button type="button" onClick={loadOmniWikiReference} disabled={formLocked}>Cargar referencia OMNI WIKI · 1884</button>
      <button type="button" onClick={loadElevenLabsReference} disabled={formLocked}>Cargar referencia OMNI VOZ · ElevenLabs</button>
      <button type="button" onClick={() => void loadWorkspace()} disabled={busy}>
        {mode === "agents" ? "Actualizar conexión y trabajos" : "Actualizar conexión"}</button>
    </div>
    <div className="rea-grid">
      <label>Aplicación de referencia
        <input value={appName} maxLength={80} placeholder="Nombre de referencia" disabled={formLocked}
          onChange={event => { setAppName(event.target.value); invalidate(); }} />
      </label>
      <label>Plataforma
        <select value={platform} disabled={formLocked} onChange={event => { setPlatform(event.target.value as ReaPlatform); invalidate(); }}>
          <option value="web">Web</option><option value="ios">iPhone / iOS</option>
          <option value="android">Android</option><option value="windows">Windows</option>
          <option value="macos">macOS</option><option value="other">Otra</option>
        </select>
      </label>
    </div>
    <label>¿Qué quieres desarrollar en OMNISYNIA?
      <textarea data-writing="literal" value={goal} maxLength={650} rows={3} disabled={formLocked}
        placeholder="Describe la función original que quieres construir y su destino dentro de ONE CORE."
        onChange={event => { setGoal(event.target.value); invalidate(); }} />
    </label>
    <label>Pantallas, controles y comportamiento
      <textarea data-writing="literal" className="rea-observations" value={observations} rows={9} maxLength={22000}
        spellCheck={false} disabled={formLocked}
        onChange={event => { setObservations(event.target.value); invalidate(); }} />
    </label>
    <p className="rea-format">Una línea por elemento: tipo | nombre | acción | evidencia.
      Admite observed, documentation o hypothesis. Inicia cada pantalla con «Pantalla: nombre».</p>
    <label className="rea-consent"><input type="checkbox" checked={rightsConfirmed} disabled={formLocked}
      onChange={event => { setRightsConfirmed(event.target.checked); invalidate(); }} />
      Confirmo que puedo usar estas observaciones para desarrollar una implementación original,
      sin copiar código ni recursos protegidos.</label>
    {mode === "agents" && <div className="rea-actions rea-primary-actions">
      <button type="button" onClick={() => void startAgents()}
        disabled={formLocked || !registry || !rightsConfirmed || !appName.trim() || !goal.trim()}>
        {running ? "Agentes trabajando…" : "Poner agentes a trabajar"}</button>
      {job && <button type="button" onClick={() => void consultJob(job.id)} disabled={busy}>Consultar trabajo</button>}
      {running && <button type="button" onClick={() => void cancelJob()} disabled={busy}>Cancelar trabajo</button>}
    </div>}
    {mode !== "agents" && <details className="rea-manual" open><summary>Analizar y generar por separado</summary>
      <div className="rea-actions">
        <button type="button" onClick={() => void analyze()} disabled={formLocked || !engine || !rightsConfirmed}>
          Analizar con REA</button>
        <button type="button" onClick={() => void generate()} disabled={formLocked || !report}>Generar prototipo TSX</button>
      </div>
    </details>}
    {notice && <p className="rea-notice" role="status">{notice}</p>}
    {job && <section className="rea-job" aria-label="Estado del trabajo REA" aria-live="polite">
      <h3>{jobLabels[job.status]}</h3><p>{job.goal}</p>
      <p>Solicitudes a proveedores externos: {job.externalRequests}.
        {running && following ? " Seguimiento automático activo mientras esta pantalla esté visible." : ""}</p>
      {job.error && <p>{job.error}</p>}
      {job.verification && <div className="rea-verification">
        <h4>Revisión estructural {job.verification.passed ? "superada" : "con pendientes"}</h4>
        <ul>{job.verification.checks.map(check => <li key={check.id}>
          <strong>{check.passed ? "Correcto" : "Revisar"}: {check.label}</strong><span>{check.detail}</span>
        </li>)}</ul>
        {job.verification.pending.length > 0 && <><h4>Pendiente para integrar</h4>
          <ul>{job.verification.pending.map((item, index) => <li key={index}>{item}</li>)}</ul></>}
      </div>}
    </section>}
    {generated && <div className="rea-actions rea-result-actions">
      <button type="button" onClick={download}>Descargar TSX</button>
      <button type="button" onClick={() => void copy()}>Copiar código</button>
    </div>}
    {report && <section className="rea-report" aria-label="Informe REA">
      <h3>Informe de funciones · {report.reference.appName}</h3>
      <p><strong>Destino sugerido:</strong> {report.targetModules.join(" · ")}</p>
      <p>{report.evidenceNote}</p>
      <p>Elementos registrados: {report.inventory.length}. Acciones sin definir: {report.undefinedActions}.</p>
      <ul>{report.inventory.map((item, index) =>
        <li key={index}><strong>{item.screen} › {item.element}</strong> · {item.evidence}
          <span>{item.action ?? "Acción por determinar"}</span></li>)}</ul>
      <p>Revisión pendiente: {report.needsReview.join(" · ")}.</p>
    </section>}
    {generated && <p className="rea-output">Prototipo original preparado. Aún no se ha ejecutado,
      conectado a operaciones reales de ONE CORE ni publicado.</p>}
    {jobs.length > 0 && <details className="rea-history"><summary>Trabajos recientes ({jobs.length})</summary>
      <div className="rea-actions">{jobs.map(item => <button type="button" key={item.id}
        disabled={busy} aria-pressed={job?.id === item.id} onClick={() => void consultJob(item.id)}>
        <strong>{jobLabels[item.status]}</strong><span>{item.goal}</span>
      </button>)}</div>
    </details>}
  </section>;
}
